import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Stack,
  Divider,
  Paper,
  Avatar,
  Container,
  CircularProgress,
  Fade,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  TrendingUp,
  VerifiedUser,
  Download,
  Visibility,
  VisibilityOff,
  ListAlt,
  PlayArrowRounded,
} from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#F4F7FE',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: '#E9EDF7' },
    '&.Mui-focused fieldset': { borderColor: '#4318FF' },
  },
  '& .MuiInputBase-input': { fontWeight: 600, color: '#2B3674' },
};

const FacturesHonoraires = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [, setLoadingIntervenants] = useState(true);
  const [intervenants, setIntervenants] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [showDetails, setShowDetails] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const resetAll = () => {
    setPreviewData(null);
    setPdfOpen(false);
    setPdfUrl(null);
    setShowDetails(true);

    setFormData({
      startDate: dayjs().startOf('month'),
      endDate: dayjs(),
      intervenantId: '',
      commission: 20,
    });
  };

  const rolePath = useMemo(() => {
    const role = Number(user?.role);
    return role === 1 || role === 2 ? 'admin' : 'intervenant';
  }, [user]);

  const [formData, setFormData] = useState({
    startDate: dayjs().startOf('month'),
    endDate: dayjs(),
    intervenantId: '',
    commission: 20,
  });

  useEffect(() => {
    if (!rolePath || !token) return;
    const loadIntervenants = async () => {
      setLoadingIntervenants(true);
      try {
        const res = await axios.get(
          `${apiUrl}/${rolePath}/intervenant/l/list_intervenant`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = res.data.result || res.data;
        setIntervenants(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erreur chargement intervenants:', err.response?.data);
      } finally {
        setLoadingIntervenants(false);
      }
    };
    loadIntervenants();
  }, [apiUrl, rolePath, token]);

  // Génération de l'aperçu JSON
  const handleGeneratePreview = async () => {
    if (!formData.startDate || !formData.endDate || !formData.intervenantId)
      return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${apiUrl}/${rolePath}/genenrate/intervenant/g/invoice/${formData.intervenantId}`,
        {
          params: {
            startTime: formData.startDate.format('DD-MM-YYYY'),
            endTime: formData.endDate.format('DD-MM-YYYY'),
            preview: 1,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPreviewData(response.data.result);
    } catch (error) {
      console.error('Erreur preview:', error);
      setPreviewData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!formData.intervenantId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${apiUrl}/${rolePath}/genenrate/intervenant/g/invoice/${formData.intervenantId}`,
        {
          params: {
            startTime: formData.startDate.format('DD-MM-YYYY'),
            endTime: formData.endDate.format('DD-MM-YYYY'),
            preview: 0, // Mode PDF
          },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob', // 👈 Très important pour recevoir du binaire
        }
      );

      // Vérifier si le blob reçu est par hasard une erreur JSON convertie en texte
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorObj = JSON.parse(text);
        console.error('Erreur serveur PDF:', errorObj);
        alert(
          'Erreur lors de la génération du PDF : ' +
            (errorObj.message || 'Erreur inconnue')
        );
        return;
      }

      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);

      setPdfUrl(fileURL);
      setPdfOpen(true);
    } catch (error) {
      console.error('Erreur Blob PDF:', error);
      alert(
        'Impossible de générer le PDF. Vérifiez la console pour plus de détails.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#F8F9FD', minHeight: '100vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        {/* --- HEADER --- */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 6 }}
          spacing={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, color: '#2B3674', letterSpacing: '-1px' }}
            >
              Note d'honoraires
            </Typography>
          </Box>
          <Chip
            icon={
              <VerifiedUser
                sx={{ color: '#01B574 !important', fontSize: '18px' }}
              />
            }
            label="Système de facturation sécurisé"
            sx={{
              bgcolor: '#E6F6F0',
              color: '#01B574',
              fontWeight: 700,
              borderRadius: '10px',
              p: 1,
            }}
          />
        </Stack>

        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '16px',
              bgcolor: '#EEF4FF',
              border: '1px solid #D6E4FF',
            }}
          >
            <Typography sx={{ fontWeight: 600, color: '#2B3674' }}>
              💡💡 Sélectionnez un intervenant ou un praticien, choisissez une
              période, puis générez l’aperçu des honoraires.
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7A99', mt: 0.5 }}>
              Les montants sont calculés automatiquement à partir des commandes
              validées sur la période sélectionnée.
            </Typography>
          </Paper>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} lg={7}>
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: '24px',
                  border: '1px solid #E9EDF7',
                  boxShadow: '0px 20px 40px rgba(112, 144, 176, 0.05)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: '#2B3674', mb: 3 }}
                >
                  Configuration
                </Typography>

                <Stack spacing={3}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: '#A3AED0',
                        ml: 1,
                        mb: 1,
                        display: 'block',
                      }}
                    >
                      CHOISIR L'INTERVENANT / PRATICIEN
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={formData.intervenantId}
                      onChange={(e) => {
                        const newId = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          intervenantId: newId,
                        }));
                        setPreviewData(null);
                        setPdfOpen(false);
                        setPdfUrl(null);
                      }}
                      sx={inputStyle}
                      renderValue={(selected) => {
                        const foundUser = intervenants.find(
                          (i) => i.id_user === selected
                        );
                        if (!foundUser) return 'Choisir un intervenant';

                        return (
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Avatar
                              src={
                                foundUser.photo_user
                                  ? `${apiUrl}/storage/workshop/${foundUser.photo_user}`
                                  : ''
                              }
                              sx={{ width: 28, height: 28 }}
                            >
                              {foundUser.nom_user?.charAt(0)}
                              {foundUser.prenom_user?.charAt(0)}
                            </Avatar>
                            <Typography sx={{ fontWeight: 700 }}>
                              {foundUser.nom_user} {foundUser.prenom_user}
                            </Typography>
                          </Stack>
                        );
                      }}
                    >
                      {intervenants.map((i) => (
                        <MenuItem key={i.id_user} value={i.id_user}>
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Avatar
                              src={
                                i.photo_user
                                  ? `${apiUrl}/storage/workshop/${i.photo_user}`
                                  : ''
                              }
                              sx={{ width: 32, height: 32 }}
                            >
                              {i.nom_user?.charAt(0)}
                              {i.prenom_user?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography
                                sx={{ fontWeight: 700, color: '#2B3674' }}
                              >
                                {i.nom_user} {i.prenom_user}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: '#A3AED0' }}
                              >
                                {i.email_user}
                              </Typography>
                            </Box>
                          </Stack>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: '#A3AED0',
                          ml: 1,
                          mb: 1,
                          display: 'block',
                        }}
                      >
                        DATE DE DÉBUT
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={formData.startDate}
                          onChange={(v) =>
                            setFormData((prev) => ({ ...prev, startDate: v }))
                          }
                          slotProps={{
                            textField: { fullWidth: true, sx: inputStyle },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: '#A3AED0',
                          ml: 1,
                          mb: 1,
                          display: 'block',
                        }}
                      >
                        DATE DE FIN
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={formData.endDate}
                          onChange={(v) =>
                            setFormData((prev) => ({ ...prev, endDate: v }))
                          }
                          slotProps={{
                            textField: { fullWidth: true, sx: inputStyle },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>

                  {/* BOUTON GÉNÉRER APERÇU */}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleGeneratePreview}
                    disabled={loading || !formData.intervenantId}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <PlayArrowRounded />
                      )
                    }
                    sx={{
                      bgcolor: '#4318FF',
                      py: 1.8,
                      borderRadius: '14px',
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: '1rem',
                      boxShadow: '0px 10px 20px rgba(67, 24, 255, 0.2)',
                      '&:hover': { bgcolor: '#3311DB' },
                    }}
                  >
                    {loading ? 'Chargement...' : "Générer l'aperçu"}
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={resetAll}
                    disabled={loading}
                    sx={{
                      mt: 2,
                      borderRadius: '14px',
                      fontWeight: 700,
                      textTransform: 'none',
                      borderColor: '#E9EDF7',
                      color: '#2B3674',
                      '&:hover': { borderColor: '#4318FF', color: '#4318FF' },
                    }}
                  >
                    Réinitialiser
                  </Button>
                </Stack>
              </Paper>

              {/* --- TABLEAU DÉTAILLÉ --- */}
              <Fade in={!!previewData}>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: '24px',
                    border: '1px solid #E9EDF7',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: '#FFF',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ListAlt sx={{ color: '#4318FF' }} />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: '#2B3674' }}
                      >
                        Prestations détaillées
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => setShowDetails(!showDetails)}
                      startIcon={
                        showDetails ? <VisibilityOff /> : <Visibility />
                      }
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {showDetails ? 'Masquer' : 'Afficher'}
                    </Button>
                  </Box>
                  {showDetails && (
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#F4F7FE' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: '#A3AED0' }}>
                            Prestation / Réf
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: '#A3AED0' }}
                          >
                            Date
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: '#A3AED0' }}
                          >
                            Quantité
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, color: '#A3AED0' }}
                          >
                            Total TTC
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, color: '#A3AED0' }}
                          >
                            Net Praticien
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody sx={{ bgcolor: '#FFF' }}>
                        {previewData?.items?.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: '#2B3674' }}
                              >
                                {item.item}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#4318FF',
                                  fontWeight: 600,
                                  display: 'block',
                                }}
                              >
                                Réf: {item.refCommand}
                              </Typography>

                              {/* 🟢 Affichage du badge Patient / Client Normal */}
                              <Box sx={{ mt: 0.5, mb: 0.5 }}>
                                {item.is_patient ? (
                                  <Chip
                                    label="Patient SOA"
                                    size="small"
                                    sx={{
                                      height: '18px',
                                      fontSize: '9px',
                                      fontWeight: 700,
                                      bgcolor: '#E6F6F0',
                                      color: '#01B574',
                                      borderRadius: '4px',
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    label="Client Normal"
                                    size="small"
                                    sx={{
                                      height: '18px',
                                      fontSize: '9px',
                                      fontWeight: 700,
                                      bgcolor: '#F4F7FE',
                                      color: '#2B3674',
                                      borderRadius: '4px',
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Affichage du prix initial de l'offre catalogue si présent */}
                              {item.prix_offre_catalogue > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#6B7A99',
                                    display: 'block',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  Prix initial offre :{' '}
                                  {Number(item.prix_offre_catalogue).toFixed(2)}{' '}
                                  €
                                </Typography>
                              )}

                              {/* Détails SOA (Prix spécial / Prix libre) */}
                              {/* Détails SOA ou Client Normal */}
                              {/* Détails SOA ou Client Normal */}
                              {item.details_financiers && (
                                <Box
                                  sx={{
                                    fontSize: '10px',
                                    color: '#6B7A99',
                                    mt: 0.5,
                                  }}
                                >
                                  {item.is_patient ? (
                                    <>
                                      {Number(
                                        item.details_financiers
                                          .prix_special_soa || 0
                                      ) > 0 && (
                                        <span>
                                          Prix Spécial SOA:{' '}
                                          {Number(
                                            item.details_financiers
                                              .prix_special_soa
                                          ).toFixed(2)}{' '}
                                          € |{' '}
                                        </span>
                                      )}
                                      {Number(
                                        item.details_financiers.prix_libre || 0
                                      ) > 0 && (
                                        <span>
                                          Prix Libre:{' '}
                                          {Number(
                                            item.details_financiers.prix_libre
                                          ).toFixed(2)}{' '}
                                          €
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <span>
                                        Commission appliquée :{' '}
                                        {item.details_financiers
                                          .taux_commission ?? 20}
                                        %{' '}
                                      </span>
                                      <span>
                                        (-
                                        {Number(
                                          item.details_financiers
                                            .commission_plateforme || 0
                                        ).toFixed(2)}{' '}
                                        €)
                                      </span>
                                    </>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: '#2B3674',
                                  fontSize: '12px',
                                }}
                              >
                                {item.date}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>
                              {item.quantity}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 700, color: '#2B3674' }}
                            >
                              {Number(item.totalTTC || 0).toFixed(2)} €
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 700, color: '#01B574' }}
                            >
                              {Number(
                                item.details_financiers
                                  ?.net_a_verser_praticien || 0
                              ).toFixed(2)}{' '}
                              €
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>
              </Fade>
            </Stack>
          </Grid>

          {/* --- RÉSUMÉ (COLONNE DROITE) --- */}
          <Grid item xs={12} lg={5}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: '24px',
                background: 'linear-gradient(135deg, #111C44 0%, #0B1437 100%)',
                color: '#FFF',
                position: 'sticky',
                top: 24,
              }}
            >
              <Stack spacing={4}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Résumé Financier
                </Typography>

                <Box
                  sx={{
                    p: 3,
                    borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: '#A3AED0', letterSpacing: 1 }}
                  >
                    CHIFFRE D'AFFAIRES BRUT
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mt: 1 }}>
                    {previewData
                      ? `${Number(previewData.totals?.brut || 0).toLocaleString()} €`
                      : '0.00 €'}
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between">
                   
                    
                  </Stack>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#01B574', fontWeight: 800 }}
                    >
                      NET À PAYER
                    </Typography>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-end"
                    >
                      <Typography
                        variant="h2"
                        sx={{ fontWeight: 900, letterSpacing: -1 }}
                      >
                        {previewData
                          ? `${Number(previewData.totals?.net_payable || 0).toLocaleString()} €`
                          : '--- €'}
                      </Typography>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(1, 181, 116, 0.2)',
                          color: '#01B574',
                        }}
                      >
                        <TrendingUp />
                      </Avatar>
                    </Stack>
                  </Box>
                </Stack>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleDownload}
                  disabled={!previewData || loading}
                  startIcon={<Download />}
                  sx={{
                    bgcolor: '#4318FF',
                    py: 2,
                    borderRadius: '16px',
                    fontWeight: 800,
                    '&:hover': { bgcolor: '#3311DB' },
                  }}
                >
                  Générer le PDF Officiel
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* MODAL DE PRÉVISUALISATION PDF */}
        <Dialog
          open={pdfOpen}
          onClose={() => {
            setPdfOpen(false);
            if (pdfUrl) URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
          }}
          fullWidth
          maxWidth="lg"
        >
          <Box
            sx={{ height: '85vh', display: 'flex', flexDirection: 'column' }}
          >
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #eee',
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>Aperçu PDF</Typography>
              <Button
                onClick={() => {
                  setPdfOpen(false);
                  if (pdfUrl) URL.revokeObjectURL(pdfUrl);
                  setPdfUrl(null);
                }}
              >
                Fermer
              </Button>
            </Box>
            <Box sx={{ flex: 1 }}>
              {pdfUrl && (
                <iframe
                  src={pdfUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="Aperçu de la facture PDF"
                />
              )}
            </Box>
          </Box>
        </Dialog>
      </Container>
    </Box>
  );
};

export default FacturesHonoraires;
